/**
 * Dash Photo Capture Component
 * 
 * Special UI for items with shared_key='DASH_PHOTO'
 * Handles OCR extraction and mileage confirmation
 */

import { useState, useRef } from 'react';
import { Camera, Loader2, Check, Edit2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { uploadInspectionEvidence, getEvidenceSignedUrl } from '@/lib/inspections/evidenceUpload';
import { extractDashboardInfo, isValidOdometer, type DashboardOcrResult } from '@/lib/inspections/dashboardOcr';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InspectionEvidence, UpsertItemParams } from '@/hooks/useInspectionExecution';
import { cn } from '@/lib/utils';

interface DashPhotoCaptureProps {
  shopId: string;
  ticketId: string;
  inspectionItemId: string | undefined;
  templateItemId: string;
  systemKey: string;
  itemKey: string;
  evidence: InspectionEvidence[];
  currentMileage: number | null | undefined;
  isEditable: boolean;
  onUpdate: (params: Omit<UpsertItemParams, 'templateItemId' | 'systemKey' | 'itemKey' | 'sharedKey'>) => Promise<void>;
  onEvidenceChange: () => void;
}

export function DashPhotoCapture({
  shopId,
  ticketId,
  inspectionItemId,
  templateItemId,
  systemKey,
  itemKey,
  evidence,
  currentMileage,
  isEditable,
  onUpdate,
  onEvidenceChange,
}: DashPhotoCaptureProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingOcr, setIsProcessingOcr] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [ocrResult, setOcrResult] = useState<DashboardOcrResult | null>(null);
  const [editableMileage, setEditableMileage] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get signed URL for existing evidence
  const latestEvidence = evidence[evidence.length - 1];
  const { data: signedUrl } = useQuery({
    queryKey: ['evidence-url', latestEvidence?.id],
    queryFn: () => latestEvidence ? getEvidenceSignedUrl(latestEvidence.storage_path) : null,
    enabled: !!latestEvidence,
    staleTime: 1000 * 60 * 30,
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !inspectionItemId) return;

    setIsUploading(true);
    
    try {
      // Upload the file
      await uploadInspectionEvidence({
        shopId,
        ticketId,
        inspectionItemId,
        file,
        source: 'technician',
        sharedKey: 'DASH_PHOTO',
        itemKey,
      });

      onEvidenceChange();
      queryClient.invalidateQueries({ queryKey: ['ticket-inspection-evidence'] });

      // Run OCR
      setIsProcessingOcr(true);
      const result = await extractDashboardInfo(file);
      setOcrResult(result);
      
      // If we found candidates, show confirmation dialog
      if (result.odometer_candidates.length > 0) {
        setEditableMileage(result.selected_odometer?.toString() || '');
        setShowConfirmDialog(true);
      } else {
        toast({
          title: 'No mileage detected',
          description: 'Could not detect mileage from the photo. You can enter it manually.',
        });
        setEditableMileage('');
        setShowConfirmDialog(true);
      }
    } catch (error) {
      console.error('Dash photo capture failed:', error);
      toast({
        title: 'Capture failed',
        description: error instanceof Error ? error.message : 'Failed to capture dash photo.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setIsProcessingOcr(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmMileage = async () => {
    const mileage = parseInt(editableMileage, 10);
    
    if (!isValidOdometer(mileage)) {
      toast({
        title: 'Invalid mileage',
        description: 'Please enter a valid mileage value.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    
    try {
      // Update tickets.odometer_miles
      const { error: ticketError } = await supabase
        .from('tickets')
        .update({ odometer_miles: mileage })
        .eq('id', ticketId);

      if (ticketError) throw ticketError;

      // Store OCR result in inspection item
      await onUpdate({
        severity: 'PASS',
        result: {
          ocr_text: ocrResult?.raw_text || '',
          odometer: mileage,
          warnings_text: ocrResult?.warnings_detected || [],
          processing_time_ms: ocrResult?.processing_time_ms,
        },
      });

      toast({
        title: 'Mileage saved',
        description: `Odometer reading saved: ${mileage.toLocaleString()} mi`,
      });

      setShowConfirmDialog(false);
      
      // Invalidate ticket queries to show updated mileage
      queryClient.invalidateQueries({ queryKey: ['ticket'] });
    } catch (error) {
      console.error('Failed to save mileage:', error);
      toast({
        title: 'Failed to save mileage',
        description: error instanceof Error ? error.message : 'An error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-sm">Dashboard Photo</h4>
          <p className="text-xs text-muted-foreground">
            Capture the dashboard to record mileage
          </p>
        </div>
        {currentMileage && (
          <div className="text-right">
            <span className="text-xs text-muted-foreground">Current:</span>
            <p className="font-medium">{currentMileage.toLocaleString()} mi</p>
          </div>
        )}
      </div>

      {/* Preview of existing photo */}
      {latestEvidence && signedUrl && (
        <div className="relative w-full h-32 rounded-md overflow-hidden border">
          <img 
            src={signedUrl} 
            alt="Dashboard" 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Capture button */}
      {isEditable && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            type="button"
            variant={latestEvidence ? 'outline' : 'default'}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isProcessingOcr || !inspectionItemId}
            className="w-full gap-2"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : isProcessingOcr ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Reading mileage...
              </>
            ) : (
              <>
                <Camera className="h-4 w-4" />
                {latestEvidence ? 'Retake Photo' : 'Capture Dashboard'}
              </>
            )}
          </Button>
        </>
      )}

      {/* Warnings detected display */}
      {ocrResult?.warnings_detected && ocrResult.warnings_detected.length > 0 && (
        <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
          <div>
            <span className="font-medium text-yellow-800">Warnings detected:</span>
            <p className="text-yellow-700">{ocrResult.warnings_detected.join(', ')}</p>
          </div>
        </div>
      )}

      {/* Mileage confirmation dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Mileage Reading</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {ocrResult?.odometer_candidates && ocrResult.odometer_candidates.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                Detected mileage from photo. Please confirm or edit:
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Could not automatically detect mileage. Please enter manually:
              </p>
            )}

            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={editableMileage}
                onChange={(e) => setEditableMileage(e.target.value)}
                placeholder="Enter mileage"
                className="text-lg font-mono"
              />
              <span className="text-muted-foreground">mi</span>
            </div>

            {ocrResult?.odometer_candidates && ocrResult.odometer_candidates.length > 1 && (
              <div className="text-xs text-muted-foreground">
                <span>Other candidates: </span>
                {ocrResult.odometer_candidates.slice(1, 4).map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setEditableMileage(c.toString())}
                    className="underline mr-2 hover:text-primary"
                  >
                    {c.toLocaleString()}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMileage} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm Mileage
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
